/**
 * FavouritesContext — single shared source of truth for the current
 * user's favourite event IDs and civilian-night IDs.
 *
 * Previously the `useFavourites` hook held its own useState which meant
 * every component that called it got an ISOLATED copy of the set — so
 * toggling a heart inside `FavouriteButton` updated that instance's
 * state but the Calendar's separate instance had no idea, and the
 * favourites-only filter didn't refresh until you reloaded the page.
 *
 * Lifting into a Provider that's mounted once at app root fixes it.
 *
 * Public surface (unchanged from the old hook, so all existing call
 * sites keep working):
 *   const { events, civilians, loaded, toggle, isFavourite } = useFavourites()
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = () => {
  const t = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const FavouritesContext = createContext({
  events: new Set(),
  civilians: new Set(),
  loaded: false,
  toggle: async () => null,
  isFavourite: () => false,
});

export function FavouritesProvider({ children }) {
  const { user } = useAuth();
  const [events, setEvents] = useState(new Set());
  const [civilians, setCivilians] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) {
        if (!cancelled) {
          setEvents(new Set());
          setCivilians(new Set());
          setLoaded(true);
        }
        return;
      }
      try {
        const { data } = await axios.get(`${API}/favourites/mine`, {
          headers: authHeader(),
        });
        if (cancelled) return;
        setEvents(new Set(data.event_ids || []));
        setCivilians(new Set(data.civilian_ids || []));
      } catch {
        if (!cancelled) {
          setEvents(new Set());
          setCivilians(new Set());
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const toggle = useCallback(async (kind, id) => {
    if (!user) return null;
    const setter = kind === 'civilian' ? setCivilians : setEvents;
    const current = kind === 'civilian' ? civilians : events;
    const wasFav = current.has(id);
    const optimistic = new Set(current);
    if (wasFav) optimistic.delete(id); else optimistic.add(id);
    setter(optimistic);
    try {
      const path = kind === 'civilian'
        ? `${API}/favourites/civilian/${id}/toggle`
        : `${API}/favourites/events/${id}/toggle`;
      const { data } = await axios.post(path, {}, { headers: authHeader() });
      const confirmed = new Set(current);
      if (data.favourited) confirmed.add(id); else confirmed.delete(id);
      setter(confirmed);
      return data.favourited;
    } catch (e) {
      setter(current);
      throw e;
    }
  }, [user, events, civilians]);

  const isFavourite = useCallback(
    (kind, id) => (kind === 'civilian' ? civilians.has(id) : events.has(id)),
    [events, civilians]
  );

  return (
    <FavouritesContext.Provider value={{ events, civilians, loaded, toggle, isFavourite }}>
      {children}
    </FavouritesContext.Provider>
  );
}

export default function useFavourites() {
  return useContext(FavouritesContext);
}
